import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import InteriorResidents from './InteriorResidents';
import ListeningSeats from './ListeningSeats';
import EnsembleDirector from './EnsembleDirector';
import { useFrame, useThree } from '@react-three/fiber';
import { Text, Html, useGLTF } from '@react-three/drei';
import SafeMount from './SafeMount';
import { ktx2Extend } from '../utils/ktx2';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import MeshInterior from './MeshInterior';
import SplatWorld from './SplatWorld';
import VideoPanel from './VideoPanel';
import RoomCollider from './RoomCollider';
import PlaceableBadges from './PlaceableBadges';
import useRoomPlaceables from '../hooks/useRoomPlaceables';
import RecordStore from './RecordStore';
import NewsTv from './NewsTv';
import AnimatedResident from './AnimatedResident';
import { beatPulse } from '../utils/worldBeat';
import { beatBridge } from '../state/beatBridge';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, authUser, WORLD_PERFORMER, IS_MOBILE, VIDEO_OFF, assetV } from '../config';
import RecordPlaques from './RecordPlaques';
import { useRemoteBodies } from '../services/bodyRigs';

// A 2.5D resident — stylized figure standing at the mixing board.
// In the full pipeline these are Gemini-generated sprite characters (v0.2 §7);
// the slice renders a placeholder body with the resident's real name.
function Resident({ name, position, scale = 1 }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.y = Math.sin(beatBridge.phase * Math.PI * 2) * 0.06;
    }
  });
  return (
    <group position={position} scale={scale}>
      <mesh ref={ref} castShadow>
        <capsuleGeometry args={[0.32, 1.0, 6, 16]} />
        <meshStandardMaterial color="#2e2a3a" roughness={0.7} metalness={0.2} />
      </mesh>
      <Text position={[0, 1.5, 0]} fontSize={0.28} color="#ffd27d" anchorX="center">
        {name}
      </Text>
    </group>
  );
}

// Shared plumbing for a Marble splat room: loads the room's manifest, tracks
// whether the splat is live (so the procedural fallback shell can hide), and
// pushes a manifest-shipped designed spawn into the camera rig when present.
function useSplatRoom(roomId) {
  const [splatReady, setSplatReady] = useState(false);
  const [manifest, setManifest] = useState(null);
  // The splat's as-fitted {position, scale} — the frame a solid twin
  // (RoomCollider) must be mounted in to line up with what you see.
  const [fit, setFit] = useState(null);
  const setSpawnOverride = useWorldStore((s) => s.setSpawnOverride);
  const room = ROOMS[roomId];
  // A room with no manifest has no splat to load — mount nothing rather than
  // fetch `undefined` and report an error on every visit.
  const splat = room?.manifest && (
    <SplatWorld
      manifestUrl={room.manifest}
      onLoaded={(m, fitted) => {
        setSplatReady(true);
        setManifest(m);
        setFit(fitted || null);
        if (m?.spawn?.pos && m?.spawn?.look) setSpawnOverride(m.spawn);
      }}
      onError={() => setSplatReady(false)}
    />
  );
  return { splat, splatReady, manifest, fit };
}

// Mixing board — the diegetic "make music" surface (song generation = the board).
function MixingBoard({ energy }) {
  const glow = useRef();
  useFrame(() => {
    if (glow.current) {
      glow.current.material.emissiveIntensity = 0.8 + Math.abs(Math.sin(beatBridge.phase * Math.PI * 2)) * (0.6 + energy);
    }
  });
  return (
    <group position={[0, 0, -3]}>
      <mesh castShadow position={[0, 0.9, 0]}>
        <boxGeometry args={[4.4, 0.35, 1.6]} />
        <meshStandardMaterial color="#1c1c22" roughness={0.6} metalness={0.4} />
      </mesh>
      <mesh ref={glow} position={[0, 1.1, 0]} rotation={[-Math.PI / 6, 0, 0]}>
        <planeGeometry args={[4.0, 1.1]} />
        <meshStandardMaterial color="#0a0a12" emissive="#ffb25e" emissiveIntensity={1.0} />
      </mesh>
      {/* Faders */}
      {Array.from({ length: 12 }).map((_, i) => (
        <mesh key={i} position={[-1.8 + i * 0.33, 1.08, 0.1]}>
          <boxGeometry args={[0.06, 0.06, 0.5]} />
          <meshStandardMaterial color="#d9d9e0" emissive="#ff5e78" emissiveIntensity={0.4} />
        </mesh>
      ))}
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[4.4, 0.9, 1.4]} />
        <meshStandardMaterial color="#26262e" roughness={0.8} />
      </mesh>
    </group>
  );
}

// A user-scanned room lifted through the RoomScan → Gemini → Marble pipeline
// ("Your Room Is The Studio"). Pure splat — the scan IS the room, so there's
// no procedural furniture and nothing but a soft light while it streams in.
//
// Plus the two things that make it a MEET venue:
//  · a DORMANT video panel — no recorded cameo, so VideoPanel renders nothing
//    until a live meet sets liveVideoEl (MeetDock), at which point the artist
//    appears on the wall of their own scanned studio. Tune with ?panel=edit.
//  · the room's SOLID twin — the Marble collider mesh mounted in the splat's
//    exact fitted frame (RoomCollider): invisible geometry to raycast, debug
//    with ?collider=1, floor-feed with ?walk=1.
function ScannedRoom({ roomId = 'room-studio' }) {
  const { splat, splatReady, manifest, fit } = useSplatRoom(roomId);
  // Rows-over-seeds (⚙ → Room placeables): a saved video_panel row wins over
  // the rooms.js default. While still seed-backed (string id) the old
  // ?panel=edit localStorage tuner keeps working; once a row exists it is the
  // arrangement and localStorage stands down. Baked MOMENT scenes
  // (moment-<postId>) have no seeds — rows only, so any moment becomes
  // screen-capable the moment a panel row is saved into it.
  const panels = useRoomPlaceables(roomId, 'video_panel');
  const vp = panels[0];
  // The room's solid twin: moment manifests carry their own (proxied)
  // collider URL; the original scanned studio keeps its deployed copy.
  const colliderUrl = manifest?.collider_mesh_url
    || (roomId === 'room-studio' ? '/worlds/room_studio_collider.glb' : null);
  return (
    <group>
      {splat}
      {vp && (
        <VideoPanel
          position={vp.pos}
          rotationY={vp.rotY}
          height={vp.size}
          billboard={vp.data?.billboard ?? false}
          storageKey={typeof vp.object_id === 'string' ? `videoPanel:${roomId}` : null}
        />
      )}
      {splatReady && fit && colliderUrl && (
        <Suspense fallback={null}>
          <RoomCollider url={colliderUrl} fit={fit} />
        </Suspense>
      )}
      {!splatReady && <ambientLight intensity={0.5} />}
    </group>
  );
}

// The studio interior: an artist is already here, mid-session.
// Presence (v0.2 §6): other listeners in this venue appear as felt figures.
// Whose face the studio ships with. The splat manifest's cameo was recorded of
// this artist, so they — and only they — can inherit it when they have no
// producer clip of their own.
const STUDIO_RESIDENT_AVATAR = ROOMS.studio?.liveChat?.avatarId ?? null;

function Studio({ energy }) {
  const { splat, splatReady, manifest } = useSplatRoom('studio');
  const producer = useWorldStore((s) => s.producer);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  // THE ENGINEER ON THE PANEL IS WHOEVER YOU PICKED (Khari, 2026-08-04: "we can
  // just swap the assets and the ID... no model, but video").
  //
  // The producer's own clip wins. Rebel inherits the room's manifest cameo
  // because that recording IS him. Anybody else without a clip gets NO cameo —
  // a dormant panel, exactly as it sits for a meet — because playing Rebel's
  // video while Devvin Keys is talking to you is worse than an empty screen.
  const mine = producer && String(producer.avatarId) === String(myAvatarId) ? producer : null;
  const cameo = (() => {
    if (!mine) return manifest?.video;                 // nobody picked: the room's own resident
    if (mine.video?.loop || mine.video?.greeting) return mine.video;
    // `producerAvatarId`, NOT `avatarId` — a stored pick puts the PLAYER's
    // avatar in `avatarId` (that is how SceneDock scopes whose pick it is), so
    // the producer's own id is carried separately.
    if (STUDIO_RESIDENT_AVATAR && Number(mine.producerAvatarId) === STUDIO_RESIDENT_AVATAR) {
      return manifest?.video;
    }
    console.log(`[studio] ${mine.name} has no cameo clip yet — panel stays dormant`);
    return null;
  })();
  // Rows-over-seeds for everything hung in this room (⚙ → Room placeables):
  // saved world_objects rows win; the rooms.js arrays are only the seeds a
  // fresh room ships with. While seed-backed the old per-type localStorage
  // tuners keep working; a saved row IS the arrangement.
  const panels = useRoomPlaceables('studio', 'video_panel');
  const vp = panels[0];
  const plaqueRows = useRoomPlaceables('studio', 'plaque');
  const resultRows = useRoomPlaceables('studio', 'result_card');
  const rowsBacked = (rows) => rows.length > 0 && typeof rows[0].object_id === 'number';
  const asPlaques = (rows) => rows.map((o) => ({ pos: o.pos, rotY: o.rotY, size: o.size }));
  return (
    <group>
      {/* The video cameo — the resident avatar in the room with you (greeting
          once, then a silent working loop). Background baked from the same
          pano as the splat, so it composites. */}
      {cameo && vp && (
        <VideoPanel
          greetingUrl={cameo.greeting}
          loopUrl={cameo.loop}
          position={vp.pos}
          rotationY={vp.rotY}
          height={vp.size}
          billboard={vp.data?.billboard ?? false}
          storageKey={typeof vp.object_id === 'string' ? 'videoPanel:studio' : null}
        />
      )}
      {/* THE WALL — released records only. A frame here is earned. */}
      {plaqueRows.length > 0 && (
        <RecordPlaques
          plaques={asPlaques(plaqueRows)}
          group="wall"
          source="released"
          storageKey={rowsBacked(plaqueRows) ? null : 'plaques:studio'}
        />
      )}
      {/* RESULT CARDS — what just came out of the room, lying where they were
          put and squaring up to you when you look at them. */}
      {resultRows.length > 0 && (
        <RecordPlaques
          plaques={asPlaques(resultRows)}
          group="results"
          source="undecided"
          storageKey={rowsBacked(resultRows) ? null : 'results:studio'}
        />
      )}

      {/* Photoreal Marble splat interior. Renders behind the procedural
          overlay props; when the splat loads we hide the placeholder room shell. */}
      {splat}

      {/* Placeholder room shell — only until the splat arrives (and as a
          fallback if the splat 404s), so the venue is never an empty void. */}
      {!splatReady && (
      <>
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[16, 12]} />
        <meshStandardMaterial color="#241a16" roughness={1} />
      </mesh>
      {[
        { p: [0, 2.5, -6], r: [0, 0, 0], s: [16, 5] },
        { p: [-8, 2.5, 0], r: [0, Math.PI / 2, 0], s: [12, 5] },
        { p: [8, 2.5, 0], r: [0, -Math.PI / 2, 0], s: [12, 5] }
      ].map((w, i) => (
        <mesh key={i} position={w.p} rotation={w.r}>
          <planeGeometry args={w.s} />
          <meshStandardMaterial color="#2c1f1a" roughness={1} />
        </mesh>
      ))}

      {/* Acoustic panels */}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh key={i} position={[-7 + i * 2, 3, -5.9]}>
          <boxGeometry args={[1, 1.4, 0.1]} />
          <meshStandardMaterial color={i % 2 ? '#3a2a24' : '#4a352c'} roughness={1} />
        </mesh>
      ))}
      </>
      )}

      {/* Overlay props were sized for the 16m placeholder shell; the fitted
          Marble room is metric (~4–6m), so compact them when the splat is live.
          Positions are eyeballed against the draft studio — tune in-scene. */}
      <group scale={splatReady ? 0.55 : 1} position={splatReady ? [0, 0, 0.6] : [0, 0, 0]}>
        <MixingBoard energy={energy} />

        {/* The resident placeholder — retired once the video cameo is live */}
        {!cameo && <Resident name="Rebel Rhythm" position={[0, 0.85, -4.2]} />}

        {/* Other people now render globally via <Peers/> at their real positions. */}
      </group>

      {/* Warm practicals */}
      <pointLight position={[0, 4, -2]} color="#ffb25e" intensity={20} distance={14} castShadow />
      <pointLight position={[-5, 3, 3]} color="#ff5e78" intensity={6} distance={10} />
    </group>
  );
}

// The Listening Room: the cozy social space — this room sells *presence*.
// Streams the district station (already playing via the broadcast layer);
// interactive prop = the glowing turntable console. Fallback shell is metric
// (~7×6m) so overlay props hold their positions when the splat arrives.
function ListeningRoom({ energy }) {
  const { splat, splatReady } = useSplatRoom('listening-room');
  const platter = useRef();
  useFrame((_, dt) => {
    if (platter.current) platter.current.rotation.y += dt * 3.5; // 33⅓-ish, sped for read
  });
  return (
    <group>
      {splat}

      {!splatReady && (
        <>
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[7, 6]} />
            <meshStandardMaterial color="#26181d" roughness={1} />
          </mesh>
          {[
            { p: [0, 1.5, -3], r: [0, 0, 0], s: [7, 3] },
            { p: [-3.5, 1.5, 0], r: [0, Math.PI / 2, 0], s: [6, 3] },
            { p: [3.5, 1.5, 0], r: [0, -Math.PI / 2, 0], s: [6, 3] }
          ].map((w, i) => (
            <mesh key={i} position={w.p} rotation={w.r}>
              <planeGeometry args={w.s} />
              <meshStandardMaterial color="#33212a" roughness={1} />
            </mesh>
          ))}
          {/* Record shelf + couch, blocked in */}
          <mesh castShadow position={[-2.4, 0.9, -2.7]}>
            <boxGeometry args={[1.8, 1.8, 0.4]} />
            <meshStandardMaterial color="#4a3040" roughness={0.9} />
          </mesh>
          <mesh castShadow position={[1.6, 0.4, 1.4]}>
            <boxGeometry args={[2.4, 0.8, 1]} />
            <meshStandardMaterial color="#5a3a44" roughness={1} />
          </mesh>
          {/* Album art on the walls — brand-ingredient stand-ins */}
          {[-1.5, 0, 1.5].map((x, i) => (
            <mesh key={i} position={[x, 1.9, -2.95]}>
              <planeGeometry args={[1, 1]} />
              <meshStandardMaterial color={['#ff5e78', '#ffb25e', '#7dd3ff'][i]} emissive={['#ff5e78', '#ffb25e', '#7dd3ff'][i]} emissiveIntensity={0.25} />
            </mesh>
          ))}

          {/* Turntable console — a blocked-in stand-in, NOT furniture.
              MOVED INSIDE THE !splatReady BLOCK (Khari, 2026-08-07: "we had
              started with simple 3D models in rooms.. we evolved to the splat,
              but it looks like it left the models in place"). The walls, floor
              and couch above were always gated; this one was not, so it kept
              rendering after the scan arrived and stood a black box with a red
              platter straight through the real armchair.
              A scanned room already HAS a turntable in it — it was photographed
              with one. */}
          <group position={[0, 0, -1.8]}>
            <mesh castShadow position={[0, 0.5, 0]}>
              <boxGeometry args={[1.4, 1.0, 0.7]} />
              <meshStandardMaterial color="#1c1c22" roughness={0.6} metalness={0.4} />
            </mesh>
            <mesh ref={platter} position={[0, 1.04, 0]}>
              <cylinderGeometry args={[0.42, 0.42, 0.04, 32]} />
              <meshStandardMaterial color="#0c0c10" roughness={0.4} metalness={0.3} emissive="#ff5e78" emissiveIntensity={0.15 + energy * 0.3} />
            </mesh>
          </group>
        </>
      )}

      {/* Other people now render globally via <Peers/> at their real positions. */}

      <pointLight position={[0, 2.4, 0]} color="#ff9a3c" intensity={10} distance={9} castShadow />
      <pointLight position={[-2.4, 1.6, -2]} color="#ff5e78" intensity={4} distance={6} />
    </group>
  );
}

// A spotlight that actually aims where told — three.js spotlights aim at a
// target Object3D that must live in the scene graph, so `target-position`
// alone silently aims at the origin.
function AimedSpot({ position, aim, ...props }) {
  const target = useMemo(() => new THREE.Object3D(), []);
  return (
    <>
      <primitive object={target} position={aim} />
      <spotLight position={position} target={target} {...props} />
    </>
  );
}

// Audio-reactive particle wash over the stage — splat light is baked, so all
// "show reactivity" lives in overlays like this (Demo Build Plan: hall recipe).
function StageParticles({ energy, accent, centerZ = -10, spreadX = 14, spreadZ = 8 }) {
  const ref = useRef();
  const { positions, seeds } = useMemo(() => {
    const n = 350;
    const positions = new Float32Array(n * 3);
    const seeds = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      positions[i * 3] = (Math.random() - 0.5) * spreadX;         // across the stage
      positions[i * 3 + 1] = 0.5 + Math.random() * 6;             // deck → rig height
      positions[i * 3 + 2] = centerZ + (Math.random() - 0.5) * spreadZ; // over the stage
      seeds[i] = Math.random();
    }
    return { positions, seeds };
  }, [centerZ, spreadX, spreadZ]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    const beat = beatPulse(); // tempo-correct swell (octave-folded, playhead-anchored)
    ref.current.material.opacity = 0.25 + beat * (0.3 + energy * 0.4);
    ref.current.material.size = 0.06 + beat * energy * 0.1;
    const pos = ref.current.geometry.attributes.position;
    for (let i = 0; i < seeds.length; i++) {
      pos.array[i * 3 + 1] += Math.sin(t * (0.6 + seeds[i]) + seeds[i] * 7) * 0.003;
    }
    pos.needsUpdate = true;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color={accent} size={0.08} transparent opacity={0.4} depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

// KnotSculpture — the spinning torus-knot particle sculpture hanging over the
// stage (ported from the classic three.js "particles from a TorusKnot" demo).
// The old r68 API is gone in three 0.180, so: ParticleSystem → Points,
// ParticleBasicMaterial → PointsMaterial, and TorusKnotGeometry's segment args
// SWAPPED meaning (modern order is radius, tube, tubularSegments-along-the-curve,
// radialSegments-around-the-tube) — the demo's 600/12 map to that order as-is.
// heightScale was dropped from the geometry entirely; we shape with p/q only.
//
// Sized to ENGULF the hall rather than sit in it: centred on the room and scaled
// past every wall, so you stand inside the knot and its strands read as orbs
// swinging around you. Only the arcs inside the room are visible — the walls
// depth-occlude the rest — so orbs sweep in through one wall and out the other.
//
// Tune live (no rebuild): ?knot=0 (off) · ?knotfill (× the room, default 1.6) ·
// ?knotspan (hard span in m, overrides fill) · ?knotx / ?knoty / ?knotz
// (re-centre) · ?knotspin (rad/sec) · ?knotsize (orb size, m) · ?knotrings
// (orbs along the curve) · ?knotbeat (0–1 beat reactivity).
// fitW/fitH/fitD are passed as plain numbers, not a tuple — an inline array prop
// is a new identity every render and would rebuild the geometry every frame.
function KnotSculpture({ position, fitW, fitH, fitD, fill = 1.6, energy = 0.5 }) {
  const ref = useRef();

  // Radial-gradient sprite (white core → cyan → deep blue → black). Black reads
  // as transparent under additive blending, so no alpha channel is needed.
  const sprite = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 16; canvas.height = 16;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.2, 'rgba(0,255,255,1)');
    g.addColorStop(0.4, 'rgba(0,0,64,1)');
    g.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 16, 16);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, []);
  useEffect(() => () => sprite.dispose(), [sprite]);

  // Authored at the demo's dimensions (176.4 × 176.4 × 96.4 units — X and Y are
  // equal, Z is the knot's THIN axis at 0.55×), then baked to metres. Scale lives
  // in the geometry, not the object, because three computes gl_PointSize from the
  // material in WORLD units and ignores object scale — a scaled group would leave
  // the orbs at their old pixel size.
  //
  // Fit is per-axis and takes the LARGEST ratio, so every axis clears the room —
  // fitting by width alone left the thin Z axis shorter than the hall is deep,
  // which is exactly how you end up seeing the knot's edges from the floor.
  const span = useMemo(() => {
    const hard = qp('knotspan', 0);
    if (hard > 0) return hard;
    const cover = Math.max(fitW / 176.4, fitH / 176.4, fitD / 96.37);
    return 176.4 * cover * fill;
  }, [fitW, fitH, fitD, fill]);

  const geometry = useMemo(() => {
    // Rings along the curve. Fewer on phones: at room scale each orb can cover a
    // lot of screen, and these are additive, so fill rate is the real ceiling.
    const rings = qp('knotrings', IS_MOBILE ? 260 : 600);
    const src = new THREE.TorusKnotGeometry(40, 28.2, Math.round(rings), 12, 5, 4);
    // Drop the index: THREE.Points draws through it, which would re-draw every
    // shared vertex ~6× (43k additive orbs instead of 7.8k) for no visual gain.
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', src.getAttribute('position').clone());
    src.dispose();
    g.computeBoundingBox();
    const size = new THREE.Vector3();
    g.boundingBox.getSize(size);
    const k = span / size.x;
    g.scale(k, k, k);
    return g;
  }, [span]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  // Orb size rides the span — at hall scale a 7cm point is invisible dust, and
  // the same number relative to the knot keeps the look constant as it's tuned.
  const baseSize = qp('knotsize', span * (IS_MOBILE ? 0.005 : 0.004));
  const spin = qp('knotspin', 0.6);         // demo ran 0.01 rad/frame @60fps
  const reactivity = qp('knotbeat', 1);
  useFrame((_, dt) => {
    if (!ref.current) return;
    ref.current.rotation.y += dt * spin;
    // Rides the same tempo-correct swell as every other reactive visual.
    const beat = beatPulse() * reactivity;
    ref.current.material.size = baseSize * (1 + beat * (0.35 + energy * 0.5));
    ref.current.material.opacity = 0.72 + beat * 0.28;
  });

  return (
    <points ref={ref} position={position} frustumCulled={false}>
      <primitive object={geometry} attach="geometry" />
      <pointsMaterial
        color="#ffffff"
        size={baseSize}
        map={sprite}
        transparent
        opacity={0.8}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}

// The Concert Hall — a REAL venue model now, not a shell around a prop.
//
// "QUEEN || Rock concert" by dark_igorek — Sketchfab, CC-BY-4.0
// (https://sketchfab.com/3d-models/queen-rock-concert-ea26ac9c64bb4fe5a4ab84a9f20ee19b)
// ⚠ CC-BY: the author credit must stay user-reachable (DOCS/WORLD/models/
// concert_hall/ATTRIBUTION.md; surface it wherever world credits land).
//
// The GLB is the Live Aid stadium scene at 1:1 METRES, rebuilt for our use by
// scripts/optimize_stage.mjs (1,978 meshes → 18 draw calls, KTX2): the four
// band members, their instruments, the LIVE AID banners and the "QUEEN 1985"
// polaroids are STRIPPED (--strip/--strip-mat — likenesses and a real event's
// branding; the bare banner scaffold stays and can carry our own art later).
// What remains: the stage (deck at y≈1.0, front edge z≈0.4, facing +z), a
// DENSE BAKED CROWD on the field (z 0…6), the stadium shell around it, and the
// lighting rig. The venue IS the room; bounds, crowd, performer and lights all
// derive from HALL_BOX.
//
// ⚠ DO NOT auto-ground/centre from Box3: a 44-tri backdrop shell spans y
// −5.2…14.2 and would hoist the field five metres. The field floor is ALREADY
// at y=0 in model coordinates — explicit offsets only.
// Dial it in live (no rebuild): ?stagescale · ?stagex / ?stagey / ?stagez ·
// ?stagerot.
const VENUE_URL = assetV(IS_MOBILE ? '/models/concert_hall_mobile.glb' : '/models/concert_hall_desktop.glb');
// The hall's scale, ONE default for every reader (model, room box, bounds,
// crowd, performer, lights all multiply by it — three call sites, and a drifted
// default between them would tear the room apart). At 1 the stadium read as a
// diorama against the roster residents; 2 was still short (Khari, 2026-08-18:
// "make it MUCH larger", then "+75%"). Stand in the crowd and tune with
// ?stagescale=N, then bake the number here.
const HALL_SCALE = 3.5;
function VenueModel() {
  // KTX2 textures need the transcoder wired into THIS load — a bare useGLTF
  // throws "setKTX2Loader must be called before loading KTX2 textures" and
  // takes the whole Canvas down (see CityModel, which hit the identical crash).
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(VENUE_URL, true, true, ktx2);
  const model = useMemo(() => scene.clone(true), [scene]);
  const scale = qp('stagescale', HALL_SCALE);                     // 1:1 — it's a real venue
  const pos = [qp('stagex', 0), qp('stagey', 0), qp('stagez', 0)];
  // dispose={null}: shares geometry/material with the useGLTF cache (see CityModel).
  return <primitive object={model} scale={scale} position={pos} rotation={[0, qp('stagerot', 0), 0]} dispose={null} />;
}
// NO useGLTF.preload here — it cannot parse KTX2 (no renderer at module scope;
// same crash as above, at import time). A plain read-through fetch warms the
// HTTP cache instead, so the in-component load parses from memory.
fetch(VENUE_URL).then((r) => r.arrayBuffer()).catch(() => { /* the real load reports failures */ });

// The rigged roster residents — OUR people in the crowd, on the field facing
// the stage. The model ships its own dense baked crowd (z 0…6); the roster
// stands in the BACK half of the field, where the baked mass thins, so real
// residents read as the faces nearest you rather than clipping through the
// thick of it. Tune live: ?avh (height override) · ?avz · ?avy · ?avrot ·
// ?avspanx / ?avspanz.
function ConcertHallAvatars() {
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    fetch('/models/avatars/roster.json', { cache: 'no-store' })
      // `test: true` entries are pipeline A/B bodies, not residents — the street
      // is where they get compared, so keep them out of the lineup.
      .then((r) => r.json()).then((d) => setRoster((d.npcs || []).filter((n) => !n.test))).catch(() => setRoster([]));
  }, []);
  /* ⚠ THE SERVER'S BODY WINS OVER roster.json's LOCAL PATH. roster.json is
     the metadata register — post, height, gait — but its GLB paths are a
     generation behind for 8 of the 14. Files, pose and clips come across
     wholesale or not at all; see withRemoteBody. */
  const bodies = useRemoteBodies(roster);
  const s = qp('stagescale', HALL_SCALE), zOff = qp('stagez', 0);
  const floorMidZ = ((HALL_BOX.fieldZMax - 1.4)) * s + zOff;
  // ?avh is an OVERRIDE, not a default. It began as a scale check — one shared
  // 1.8m so the stage could be sized against a known human — but once every
  // resident had a TUNED roster height (0.6–1.2 in world units) that constant
  // silently rendered the whole lineup 1.5–3× too large, and re-tuning on the
  // street never appeared to take. 0 = "unset" so each keeps its own size.
  const H = qp('avh', 0), Y = qp('avy', 0), ROT = qp('avrot', Math.PI);
  const Z = qp('avz', floorMidZ);
  const SPANX = qp('avspanx', Math.min((HALL_BOX.w - 6) * s, 26));
  const SPANZ = qp('avspanz', 2.6 * s);
  const hash = (n) => { const x = Math.sin(n * 127.1) * 43758.5453; return x - Math.floor(x); };
  if (!roster.length) return null;
  return (
    <Suspense fallback={null}>
      {bodies.map((n, i) => (
        <SafeMount
          key={n.slug || n.avatarId}
          label={`hall ${n.slug || n.avatarId}`}
          retryMs={30000}
          onRetry={() => useGLTF.clear([n.rigged, n.walk, n.idle].filter(Boolean))}
        >
        <AnimatedResident
          meshUrl={n.rigged}
          rigPose={n.pose || null}
          clipMap={n.clips || null}
          clipUrls={[n.walk, n.idle].filter(Boolean)}
          play="idle"
          height={H || n.height || 1.8}
          position={[(hash(i * 2 + 1) - 0.5) * SPANX, Y, Z + (hash(i * 2 + 7) - 0.5) * SPANZ]}
          rotationY={ROT + (hash(i * 3 + 5) - 0.5) * 1.4}
        />
        </SafeMount>
      ))}
    </Suspense>
  );
}

// Measured box of the optimized hall GLB — MEASURED, not from the optimizer's
// whole-model printout: the backdrop shell inflates that AABB (y −5.2…14.2), so
// these numbers come from the per-material bounds + vertex-density probes (see
// the VenueModel comment). Model coordinates ARE world coordinates (field floor
// y=0, near-centred x). Re-probe after ANY re-export; everything derives from
// this.
const HALL_BOX = {
  w: 31, h: 6.6, zMin: -12, zMax: 13,
  stageFrontZ: 0.4, // deck's audience edge — bounds keep non-admins off the stage
  fieldZMax: 6.2,   // where the crowd field ends and the stands begin — walk frontier
  deckY: 1.0,       // stage deck height (dominant y plateau, centre stage)
  deckX: 0,         // centre stage
  deckZ: -1.5,      // where the performer stands: mid-deck, facing +z
};
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

// Proxy GCS media through the API (WebGL VideoTextures are CORS-gated).
function proxiedMedia(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* passthrough */ }
  return url;
}

// PerformerImage — the COMPOSITING test: a transparent-PNG performer (a pose
// cutout) rendered with real alpha on the stage, so the figure reads against
// the hall with no rectangle. This is the still-image starting point; animating
// it to a loop comes later. Source: ?perfimg=<url> (a transparent PNG). Until
// one is set, a cyan hologram stands in. alphaTest gives a clean cutout edge.
function PerformerImage({ position, height = 3, img = null }) {
  const q = (k, d) => { const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k)); return Number.isFinite(v) ? v : d; };
  // ?perfimg overrides the room's shipped default (rooms.js concert-hall.performer.img).
  const url = useMemo(() => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perfimg')) || img || null, [img]);
  // Height + feet come from the live store (PerformerPanel), seeded from URL.
  const perf = useWorldStore((s) => s.perfTune);
  const H = perf.h;                                               // performer height (m)
  const pos = [position[0] + q('perfx', 0), position[1] + perf.y, position[2] + q('perfz', 0)];
  const [tex, setTex] = useState(null);
  const [aspect, setAspect] = useState(0.5);

  useEffect(() => {
    if (!url) return undefined;
    let dead = false;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(
      proxiedMedia(url),
      (t) => { if (dead) return; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; if (t.image?.width) setAspect(t.image.width / t.image.height); setTex(t); },
      undefined,
      () => console.warn('[performer] image failed:', url),
    );
    return () => { dead = true; };
  }, [url]);

  if (!tex) {
    return (
      <mesh position={pos}>
        <planeGeometry args={[H * 0.55, H]} />
        <meshBasicMaterial color="#7dd3ff" transparent opacity={0.45} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    );
  }
  return (
    <mesh position={pos}>
      <planeGeometry args={[H * aspect, H]} />
      {/* transparent + alphaTest: clean cutout edge, correct depth, shows the
          studio-lit figure as-authored (toneMapped off). */}
      <meshBasicMaterial map={tex} transparent alphaTest={0.5} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}

// PerformerVideo — a looping performance clip whose BLACK background is keyed
// out in-shader (luma threshold), so the figure composites against the hall.
// Source ?perfvideo=<url>; muted (the station carries audio). Shares perfTune
// height/feet with the still. Tune the cut with ?keylo / ?keyhi.
function PerformerVideo({ position, video = null }) {
  const q = (k, d) => { const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k)); return Number.isFinite(v) ? v : d; };
  // ?perfvideo overrides the backend default (CONCERT_HALL_1_PERFORMER via config).
  const url = useMemo(() => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perfvideo')) || video || null, [video]);
  const perf = useWorldStore((s) => s.perfTune);
  const H = perf.h;
  const pos = [position[0] + q('perfx', 0), position[1] + perf.y, position[2] + q('perfz', 0)];
  const [aspect, setAspect] = useState(0.5625);
  const [tex, setTex] = useState(null);
  const videoRef = useRef(null);
  // This video IS the concert's music: the HUD Music button (musicOn) mutes /
  // unmutes it, and the district station ducks while it's up so they don't clash.
  const musicOn = useWorldStore((s) => s.musicOn);
  // Duck the world sound bed while the concert plays; on exit, restore the world
  // audio to the CURRENT toggle state (so a "music-off" in the concert doesn't
  // blast the station when you step back to the street).
  useEffect(() => {
    audioEngine.duck(true);
    return () => { audioEngine.setMusicOn(useWorldStore.getState().musicOn).catch(() => {}); };
  }, []);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !musicOn;
    if (musicOn) v.play().catch(() => {}); // unmuting is a gesture-gated action; keep it rolling
  }, [musicOn, tex]);

  useEffect(() => {
    if (!url || VIDEO_OFF) return undefined;   // stills test: the screen stays dark
    const v = document.createElement('video');
    videoRef.current = v;
    v.crossOrigin = 'anonymous'; v.src = proxiedMedia(url);
    v.loop = true; v.muted = true; v.defaultMuted = true; v.playsInline = true; v.preload = 'auto';
    v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
    // Attach hidden to the DOM — some browsers won't decode/autoplay a detached
    // <video>, which left the VideoTexture black (and the luma key ate it → NO VIDEO).
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // Autoplay may still be blocked pre-gesture — retry on the next interaction.
    const onGesture = () => tryPlay();
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    const onMeta = () => { if (v.videoWidth) setAspect(v.videoWidth / v.videoHeight); };
    v.addEventListener('loadedmetadata', onMeta);
    const t = new THREE.VideoTexture(v); t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      v.removeEventListener('loadedmetadata', onMeta);
      v.pause(); v.removeAttribute('src'); v.load();
      try { document.body.removeChild(v); } catch { /* already gone */ }
      videoRef.current = null;
      t.dispose();
    };
  }, [url]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    uniforms: { map: { value: null }, keyLo: { value: q('keylo', 0.06) }, keyHi: { value: q('keyhi', 0.22) } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      uniform sampler2D map; uniform float keyLo; uniform float keyHi; varying vec2 vUv;
      void main() {
        vec4 c = texture2D(map, vUv);
        float luma = dot(c.rgb, vec3(0.299, 0.587, 0.114));
        float a = smoothstep(keyLo, keyHi, luma); // black bg → transparent, figure → opaque
        if (a < 0.02) discard;
        gl_FragColor = vec4(c.rgb, a);
      }`,
  }), []);
  useEffect(() => { if (tex) material.uniforms.map.value = tex; }, [tex, material]);
  useEffect(() => () => material.dispose(), [material]);

  if (!tex) return null;
  return (
    <mesh position={pos} material={material}>
      <planeGeometry args={[H * aspect, H]} />
    </mesh>
  );
}

// Chooser: ?perfvideo → keyed video, else ?perfimg → alpha still.
function Performer({ img, video, ...props }) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const qVideo = params?.get('perfvideo');
  const qImg = params?.get('perfimg');
  // ?perfvideo forces video, ?perfimg forces image; otherwise a configured video
  // default (CONCERT_HALL_1_PERFORMER) wins over an image default.
  const useVideo = !!qVideo || (!qImg && !!video);
  return useVideo
    ? <PerformerVideo {...props} video={video} />
    : <PerformerImage {...props} img={img} />;
}

function ConcertHall({ energy }) {
  const { splat } = useSplatRoom('concert-hall');

  // The venue GLB is the room — stage, field, crowd, stadium shell — so the
  // "room" here is just the hall's own measured box, tracking ?stagescale /
  // ?stagez / ?stagex the same way the model itself does.
  const s = qp('stagescale', HALL_SCALE), zOff = qp('stagez', 0), xOff = qp('stagex', 0);
  const stageFrontZ = HALL_BOX.stageFrontZ * s + zOff; // deck's audience edge
  // Where the performer stands + the lights aim: CENTRE STAGE, on the deck.
  // Nudge with ?perfx / ?perfz.
  const performZ = HALL_BOX.deckZ * s + zOff;
  const performX = HALL_BOX.deckX * s + xOff;
  const deckY = HALL_BOX.deckY * s;
  const room = useMemo(() => {
    const backZ = HALL_BOX.zMin * s + zOff;
    const frontZ = HALL_BOX.zMax * s + zOff;
    const sideX = (HALL_BOX.w / 2) * s;
    const h = HALL_BOX.h * s;
    return { backZ, frontZ, sideX, h, midZ: (backZ + frontZ) / 2, w: sideX * 2, d: frontZ - backZ };
  }, [s, zOff]);

  // Knot sculpture: centred in the room and scaled LARGER than it on every axis,
  // so the viewer stands inside the knot and never sees its extremities. The fit
  // is derived from the room box (which itself tracks ?stagew), so tuning the
  // stage re-sizes the sculpture with the hall. Centred at half the wall height:
  // strands sweep from under the floor to over the ceiling, and the walls crop
  // everything but the arcs crossing the room.
  const knotFill = qp('knotfill', 1.6);
  const knotPos = [qp('knotx', 0), qp('knoty', room.h * 0.5), qp('knotz', room.midZ)];

  // Bounds: keep the viewer ON THE FIELD, in the crowd — and OFF THE STAGE for
  // non-admins, who stop at the deck's audience edge; admins (and later
  // "performers" cleared for a live show) may walk up onto the deck.
  // CameraBounds (PlanetScene) clamps the camera to these each frame. The walk
  // frontier is the field's edge (fieldZMax), not the model's — past it there
  // are only stands and shell, and the baked crowd is what you're here to
  // stand in.
  const isAdmin = useMemo(() => authUser().isAdmin, []);
  const setRoomBounds = useWorldStore((st) => st.setRoomBounds);
  const setBoundsEnabled = useWorldStore((st) => st.setBoundsEnabled);
  useEffect(() => {
    const minZ = isAdmin ? room.backZ + 0.4 : stageFrontZ + 0.4; // non-admin can't step onto the deck
    const maxZ = HALL_BOX.fieldZMax * s + zOff;
    setRoomBounds({ min: [xOff - (room.sideX - 0.5), 0.4, minZ], max: [xOff + room.sideX - 0.5, room.h - 0.6, maxZ] });
    setBoundsEnabled(true);
    return () => setRoomBounds(null);
  }, [isAdmin, room.backZ, room.sideX, room.h, stageFrontZ, s, zOff, xOff, setRoomBounds, setBoundsEnabled]);
  return (
    <group>
      {splat}

      {/* The venue itself — stage, field, baked crowd, stadium shell. Renders
          whether or not a splat is present. Tune with ?stagescale / ?stagez /
          ?stagerot. */}
      <VenueModel />

      {/* Our residents in the crowd, back of the field, facing the stage. Tune
          with ?avh / ?avz / ?avy / ?avrot / ?avspanx / ?avspanz. */}
      <ConcertHallAvatars />

      {/* Void insurance: a dark ground plane just BELOW the field (y=−0.08 vs
          0), so any sightline through a gap in the model reads as shadow
          instead of skybox. The venue's field is the real floor. */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.08, room.midZ]}>
        <planeGeometry args={[Math.max(room.w * 4, 120), Math.max(room.d * 2, 120)]} />
        <meshStandardMaterial color="#0e0e14" roughness={1} />
      </mesh>

      {/* The performer — a transparent-PNG pose cutout composited CENTRE STAGE,
          feet on the deck (?perfimg=<url>; falls back to a hologram until set).
          position.y carries the SCALED deck height, and perf.y is an offset ON
          TOP of it (seeded h/2 = feet-on-deck) — so ?stagescale can change and
          the performer rides the deck instead of stranding at an absolute y.
          Nudge ?perfx / ?perfz; PerformerPanel drives height/feet live. */}
      <Suspense fallback={null}>
        <Performer
          position={[performX, deckY, performZ]}
          img={WORLD_PERFORMER.img || ROOMS['concert-hall'].performer?.img}
          video={WORLD_PERFORMER.video}
        />
      </Suspense>
      <pointLight position={[performX, deckY + 2.6, performZ]} color="#7dd3ff" intensity={14} distance={14} />

      {/* The particle sculpture — centred on the hall and bigger than it, so its
          orbs circle THROUGH the room around you, breathing on the beat.
          Off with ?knot=0; re-size with ?knotfill / ?knotspan / ?knotsize. */}
      {qp('knot', 1) !== 0 && (
        <KnotSculpture
          position={knotPos}
          fitW={room.w}
          fitH={room.h}
          fitD={room.d}
          fill={knotFill}
          energy={energy}
        />
      )}

      {/* Stage wash + reactive particles — the "show" layer, over the stage and
          the front of the field, sized to the stadium. Spots aim at the deck
          from out over the crowd, the way front-of-house rigs do. */}
      <StageParticles energy={energy} accent="#7dd3ff" centerZ={performZ} spreadX={Math.min(room.w * 0.6, 30)} spreadZ={10} />
      <AimedSpot position={[-7, 7.5, performZ + 7]} aim={[performX, deckY + 1.2, performZ]} angle={0.5} penumbra={0.6} color="#ff5e78" intensity={30} distance={22} />
      <AimedSpot position={[7, 7.5, performZ + 7]} aim={[performX, deckY + 1.2, performZ]} angle={0.5} penumbra={0.6} color="#ffb25e" intensity={30} distance={22} />

      {/* Other people now render globally via <Peers/> at their real positions. */}

      <ambientLight intensity={0.12} color="#3a3a55" />
    </group>
  );
}

// ── The Cinema ──────────────────────────────────────────────────────────────
// A black-box theater with one big flat 16:9 IMAX screen on the back wall,
// playing the /listen music videos back-to-back WITH sound (the HUD Music button
// mutes/unmutes; the district station ducks underneath). Room geometry:
const THEATER = {
  sideX: 13, backZ: -15, frontZ: 13, h: 15,
  screen: { w: 18, cy: 6.5, z: -14.8 }, // 18×10.125 (16:9), raised, on back wall
};

// TheaterScreen — fetches the music-video playlist, plays each clip in turn on a
// VideoTexture, and letterboxes the actual video aspect inside the fixed 16:9
// IMAX frame. Signed GCS URLs expire ~60min, so we re-fetch the playlist each
// time we loop back to the top (a full 12-clip loop is well under that).
function TheaterScreen() {
  const { w, cy, z } = THEATER.screen;
  const frameH = (w * 9) / 16;
  const musicOn = useWorldStore((s) => s.musicOn);
  const [videos, setVideos] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [tex, setTex] = useState(null);
  const [vidAspect, setVidAspect] = useState(16 / 9);
  const [label, setLabel] = useState('');
  const videoRef = useRef(null);
  const lenRef = useRef(0);

  const fetchPlaylist = () =>
    fetch(`${API_BASE}/api/music-videos/discover?limit=12`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { const v = d?.videos?.filter((x) => x.gcs_url) || []; if (v.length) { lenRef.current = v.length; setVideos(v); } })
      .catch(() => {});

  useEffect(() => { fetchPlaylist(); }, []);
  useEffect(() => { lenRef.current = videos.length; }, [videos]);

  // On leaving the cinema, clear the movie foreground AND restore the base to the
  // CURRENT Music toggle. Without the setMusicOn, clearForeground→resumeBase would
  // replay the station regardless of `musicOn` — desyncing the mute button (you
  // muted in the cinema, but the street came back playing). setMusicOn is
  // authoritative; the playBroadcast guard cancels resumeBase's stale play.
  useEffect(() => () => {
    audioEngine.clearForeground();
    audioEngine.setMusicOn(useWorldStore.getState().musicOn).catch(() => {});
  }, []);

  // Build the <video> for the current clip; on end, advance (wrap → re-fetch for
  // fresh signed URLs). Full teardown/rebuild per clip keeps it simple.
  useEffect(() => {
    if (!videos.length) return undefined;
    const cur = videos[cursor % videos.length];
    setLabel(`${cur.track_title || cur.title || ''}${cur.avatar_name ? '  ·  ' + cur.avatar_name : ''}`.trim());
    const v = document.createElement('video');
    videoRef.current = v;
    v.crossOrigin = 'anonymous';
    v.src = proxiedMedia(cur.gcs_url);
    v.loop = false;
    v.muted = !useWorldStore.getState().musicOn;
    v.playsInline = true; v.preload = 'auto';
    v.setAttribute('playsinline', '');
    if (v.muted) v.setAttribute('muted', '');
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    // This clip now owns the venue's sound bed (station goes silent under it);
    // audibility follows the Music toggle via setForegroundMuted below.
    audioEngine.setForeground(v);

    const advance = () => setCursor((c) => {
      const nc = c + 1;
      if (nc >= (lenRef.current || 1)) { fetchPlaylist(); return 0; }
      return nc;
    });
    const onMeta = () => { if (v.videoWidth) setVidAspect(v.videoWidth / v.videoHeight); };
    v.addEventListener('ended', advance);
    v.addEventListener('error', advance);
    v.addEventListener('loadedmetadata', onMeta);
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const onGesture = () => tryPlay();
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);

    const t = new THREE.VideoTexture(v); t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      v.removeEventListener('ended', advance);
      v.removeEventListener('error', advance);
      v.removeEventListener('loadedmetadata', onMeta);
      v.pause(); v.removeAttribute('src'); v.load();
      try { document.body.removeChild(v); } catch { /* already gone */ }
      videoRef.current = null;
      t.dispose();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, cursor]);

  // Music button drives the movie's audibility through the mixer (which also
  // keeps the station silenced underneath and yields to hover-news).
  useEffect(() => {
    audioEngine.setForegroundMuted(!musicOn);
  }, [musicOn, tex]);

  // "Contain" the real video aspect inside the fixed 16:9 frame (letterbox).
  const frameAspect = 16 / 9;
  const inW = vidAspect >= frameAspect ? w : frameH * vidAspect;
  const inH = vidAspect >= frameAspect ? w / vidAspect : frameH;

  return (
    <group position={[0, cy, z]}>
      {/* Frame bezel */}
      <mesh position={[0, 0, -0.06]}>
        <planeGeometry args={[w + 0.8, frameH + 0.8]} />
        <meshStandardMaterial color="#050507" roughness={1} />
      </mesh>
      {/* Black screen backing (fills the frame; letterbox bars show through) */}
      <mesh position={[0, 0, -0.03]}>
        <planeGeometry args={[w, frameH]} />
        <meshBasicMaterial color="#000000" toneMapped={false} />
      </mesh>
      {/* The video, sized to its true aspect within the frame */}
      {tex && (
        <mesh>
          <planeGeometry args={[inW, inH]} />
          <meshBasicMaterial map={tex} toneMapped={false} />
        </mesh>
      )}
      {/* Now-playing caption under the screen */}
      {label && (
        <Text position={[0, -(frameH / 2) - 0.85, 0]} fontSize={0.62} color="#c8a2ff" anchorX="center" anchorY="middle" maxWidth={w} outlineWidth={0.006} outlineColor="#000">
          {label}
        </Text>
      )}
      {/* Soft screen glow spilling into the house */}
      <pointLight position={[0, 0, 4]} color="#9a7bff" intensity={10} distance={26} />
    </group>
  );
}

// A block of cinema seats — cheap silhouettes so the house reads as a theater
// without a model. Rows recede in +z (toward the spawn); the screen is on the
// back wall at −z, so the seats FACE −z (backrest on the +z side). Seated flat
// on the floor (no float).
function TheaterSeats() {
  const rows = 6, perRow = 11, gapX = 1.7, gapZ = 1.9, z0 = -2;
  const seats = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < perRow; c++) {
      const x = (c - (perRow - 1) / 2) * gapX;
      const z = z0 + r * gapZ;
      seats.push([x, 0, z]);
    }
  }
  return (
    <group>
      {seats.map((p, i) => (
        <group key={i} position={p}>
          {/* cushion — bottom rests on the floor (~y 0.03) */}
          <mesh castShadow position={[0, 0.28, 0]}><boxGeometry args={[1.0, 0.5, 0.9]} /><meshStandardMaterial color="#241826" roughness={0.9} /></mesh>
          {/* backrest — on the +z side so the occupant faces −z toward the screen */}
          <mesh position={[0, 0.72, 0.42]}><boxGeometry args={[1.0, 0.8, 0.16]} /><meshStandardMaterial color="#2e1f30" roughness={0.95} /></mesh>
        </group>
      ))}
    </group>
  );
}

function Theater() {
  const { sideX, backZ, frontZ, h } = THEATER;
  const midZ = (backZ + frontZ) / 2, w = sideX * 2, d = frontZ - backZ;

  // Bounds: inside the walls, and kept ~4m off the screen wall so the viewer
  // can't clip through the screen.
  const setRoomBounds = useWorldStore((st) => st.setRoomBounds);
  const setBoundsEnabled = useWorldStore((st) => st.setBoundsEnabled);
  useEffect(() => {
    setRoomBounds({ min: [-(sideX - 1), 0.4, backZ + 4], max: [sideX - 1, h - 1.5, frontZ - 1] });
    setBoundsEnabled(true);
    return () => setRoomBounds(null);
  }, [sideX, backZ, frontZ, h, setRoomBounds, setBoundsEnabled]);

  return (
    <group>
      {/* Black-box shell */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, midZ]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#0a0810" roughness={1} />
      </mesh>
      {[
        { p: [0, h / 2, backZ], r: [0, 0, 0], s: [w, h] },
        { p: [-sideX, h / 2, midZ], r: [0, Math.PI / 2, 0], s: [d, h] },
        { p: [sideX, h / 2, midZ], r: [0, -Math.PI / 2, 0], s: [d, h] },
        { p: [0, h / 2, frontZ], r: [0, Math.PI, 0], s: [w, h] },
      ].map((wall, i) => (
        <mesh key={i} position={wall.p} rotation={wall.r}>
          <planeGeometry args={wall.s} />
          <meshStandardMaterial color="#120e18" roughness={1} />
        </mesh>
      ))}
      {/* Ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, h, midZ]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#0a0810" roughness={1} />
      </mesh>

      <TheaterScreen />
      <TheaterSeats />

      {/* Ambient News-podcast flatscreens down the side walls — the same
          visual_timeline slideshow the /listen podcast plays, synced across every
          TV by the shared world clock (services/newsPodcast). Muted background. */}
      {[-1, 1].map((sx) => [1, 8].map((zz, i) => (
        <NewsTv
          key={`news-${sx}-${i}`}
          position={[sx * (sideX - 0.12), 3.4, zz]}
          rotationY={sx < 0 ? Math.PI / 2 : -Math.PI / 2}
          width={2.8}
        />
      )))}

      {/* Dim house lights — sconces down the side walls */}
      {[-1, 1].map((sx) => [-8, 0, 8].map((zz, i) => (
        <pointLight key={`${sx}-${i}`} position={[sx * (sideX - 0.6), 3.4, zz]} color="#7a5cff" intensity={2.2} distance={9} />
      )))}
      <ambientLight intensity={0.08} color="#3a2a55" />
    </group>
  );
}

export default function Interior({ energy = 0.5 }) {
  const interior = useWorldStore((s) => s.interior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const isHall = interior === 'concert-hall';

  return (
    <group>
      {/* Numbered tags on everything placed in this room — the in-scene half
          of ⚙ → Room placeables. Renders nothing unless that tool is on. */}
      <PlaceableBadges />
      {interior === 'studio' && <Studio energy={energy} />}
      {interior === 'listening-room' && <ListeningRoom energy={energy} />}
      {interior === 'record-store' && <RecordStore />}
      {interior === 'room-studio' && <ScannedRoom />}
      {/* Baked moment scenes (rooms as data — App registers the ROOMS entry
          from the ?enter=moment-<postId> deep link). */}
      {typeof interior === 'string' && interior.startsWith('moment-') && <ScannedRoom roomId={interior} />}
      {isHall && <ConcertHall energy={energy} />}
      {interior === 'theater' && <Theater />}
      {/* Any room that declares a `model` is a GLB venue — one generic component
          for all of them, so adding an experience area is a rooms.js entry
          rather than another branch here. Suspense is per-venue: a big interior
          still loading must not blank the exit chip you need to leave by. */}
      {ROOMS[interior]?.model && (
        <Suspense fallback={null}><MeshInterior roomId={interior} /></Suspense>
      )}
      {/* Bodies standing in the room — SPLAT ROOMS ONLY. A GLB venue renders its
          own, inside MeshInterior.
          Not a tidy-up: this component sits at the WORLD ORIGIN, which is where
          a splat room is built, but a SITED venue stands at its street address.
          Residents are saved in room-local coordinates, so drawing them here put
          a body placed in the shop seventy units away in the middle of the city —
          present, correct, and nowhere you would ever look for it. MeshInterior
          renders them inside its own positioned group, where those coordinates
          mean what they say. */}
      {!ROOMS[interior]?.model && <InteriorResidents roomId={interior} />}
      {/* Banter belongs to the ROOM, not to the renderer: a splat room with a
          cast talks exactly like a mesh room with one. */}
      {!ROOMS[interior]?.model && <EnsembleDirector roomId={interior} />}
      {/* Listeners in the chairs — and the same argument as the two above.
          THE LISTENING ROOM IS A SPLAT (Khari, 2026-08-04: "we literally have a
          'listening room' on the map"), so mounting the seated bodies only on
          the mesh path meant the feature could never appear in the room it is
          named after. Seats there are placed rows rather than mesh affordances;
          `seatsFor` resolves whichever the room actually has. */}
      {!ROOMS[interior]?.model && <ListeningSeats />}
      {/* Exit chip — conventional UI where wayfinding demands it (Design Law 1 amendment) */}
      <Html position={[0, isHall || interior === 'theater' ? 6 : 2.5, 0]} center>
        <button
          onClick={exitInterior}
          style={{
            background: 'rgba(0,0,0,0.55)', color: '#ffd27d', border: '1px solid #ffb25e',
            padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 14
          }}
        >
          ← Back to the street
        </button>
      </Html>
    </group>
  );
}
